import { Article } from 't2-graphql-types';
import { FC, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import CreateCommentFormContainer from './CreateCommentForm/CreateCommentFormContainer';
import Typo from '../Typography/Typo';
import CommentsList from './CommentsList';
import { useGetComments } from './hooks/useGetComments';
import { COMMENTS_SECTION_ID } from './utils/constants';
import { CommentsListType } from './constants';

type TCommentsSectionProps = {
  articleId: Article['id'];
};

const CommentsContainer: FC<TCommentsSectionProps> = ({ articleId }) => {
  const router = useRouter();
  const {
    data: userData,
    loading: loadingAuthenticatedUserStatus,
    authenticatedUserId: userId,
  } = useAuthenticatedUser();
  const { loading, error, comments, getT2Comments, shouldFetchT2Comments } = useGetComments({
    articleId,
    userId,
    loadingAuthenticatedUserStatus,
  });

  const loadComments = () => {
    if (shouldFetchT2Comments) {
      getT2Comments();
    }
  };

  const handleReplyToCommentSubmit = () => {
    loadComments();
  };

  useEffect(() => {
    if (shouldFetchT2Comments) {
      getT2Comments();
    }
  }, [shouldFetchT2Comments]);

  useEffect(() => {
    const onHashChangeStart = (url: string) => {
      if (url.endsWith(`#${COMMENTS_SECTION_ID}`)) {
        getT2Comments();
      }
    };

    router?.events.on('hashChangeStart', onHashChangeStart);

    return () => {
      router?.events.off('hashChangeStart', onHashChangeStart);
    };
  }, [router?.events]);

  return (
    <div className="relative mb-10">
      <div id={COMMENTS_SECTION_ID} className="absolute -top-24" />
      <Typo text="Comments" variant="p2" className="mb-7 !font-semibold" />
      <CreateCommentFormContainer
        articleId={articleId}
        isVisible
        handleReplyToCommentSubmit={handleReplyToCommentSubmit}
      />
      <CommentsList
        comments={comments}
        loading={loading}
        error={error}
        userPublicAddress={userData?.user?.publicAddress as string}
        userId={userId}
        articleId={articleId}
        loadingAuthenticatedUserStatus={loadingAuthenticatedUserStatus}
        type={CommentsListType.FilteredComments}
      />
    </div>
  );
};

export default CommentsContainer;
